import { useContext, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { DataLagoonContext } from 'src/DataLagoonContext';
import { useAccount, useMsal } from '@azure/msal-react';
import { protectedResources } from 'src/authConfig';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { LinearProgress } from '@mui/material';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function EduAccountGuard({ children }: Props) {
  const { state, update } = useContext(DataLagoonContext);
  const { pathname } = useLocation();
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});

  useEffect(() => {
    if (state.me === null) {
      update({ me: -1 });
    } else if (state.me === -1) {
      update({ me: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchMeData = async () => {
      if (!account || inProgress !== 'none' || state.me !== -1) return;

      try {
        const response = await instance.acquireTokenSilent({
          scopes: protectedResources.dataLagoonApi.scopes,
          account,
        });

        const meResponse = await fetch(`${protectedResources.dataLagoonApi.endpoint}/me`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${response.accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (meResponse.status === 200) {
          const json = await meResponse.json();
          update({ me: json });
        }
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          try {
            const response = await instance.acquireTokenPopup({
              scopes: protectedResources.dataLagoonApi.scopes,
            });

            const meResponse = await fetch(`${protectedResources.dataLagoonApi.endpoint}/me`, {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${response.accessToken}`,
                'Content-Type': 'application/json',
              },
            });

            if (meResponse.status === 200) {
              const json = await meResponse.json();
              update({ me: json });
            }
          } catch (error2) {
            console.log(error2);
          }
        }
        console.log(error);
      }
    };

    fetchMeData();
  }, [account, inProgress, instance, state.me, update]);

  if (!state.me || state.me === -1) {
    return <LinearProgress />;
  }

  switch (state.me.gatesApprovalStatusID) {
    case 1: // NEW USER
      if (pathname === '/dataLagoon') {
        return <>{children}</>;
      }
      return <Navigate to="/dataLagoon" state={{ from: pathname }} replace />;
    case 3: // Approved
      return <>{children}</>;
    case 4: // Rejected
      return <Navigate to="/rejected-approval" state={{ from: pathname }} replace />;
    case 2: // Pending
    default:
      return <Navigate to="/pending-approval" state={{ from: pathname }} replace />;
  }
}
